"use client";

/**
 * The floating "Report a bug" tab and its reporting panel.
 *
 * Until now the only way to tell us something was broken was a thumbs-down on a
 * chat response or whichever survey happened to be on screen — both anchored to
 * a message, neither reachable at the moment a problem actually appears. This
 * is a way to say "this is broken" from wherever you are, without losing your
 * place.
 *
 * ── What it is not ─────────────────────────────────────────────────────────
 * Not a conversation. Sending a report does not start a build, edit a project
 * or spend a credit, and nothing here — on the client or behind it — reads what
 * the words mean. The description is carried verbatim to the team. The only
 * decisions made here are objective: is the box empty, is the file an image
 * under the size limit, is a submission already in flight.
 *
 * Mounted once, globally (app/layout.tsx). It belongs to Panda's own interface
 * and is never injected into a site a user publishes.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { v4 as uuidv4 } from "uuid";
import {
  Bug,
  CheckCircle2,
  ImagePlus,
  Loader2,
  TriangleAlert,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  MODAL_SCROLL_BODY,
  MODAL_VIEWPORT_FRAME,
} from "@/components/ui/modal-surface";
import {
  projectIdFromPathname,
  routeTagFor,
} from "@/lib/observability/route-tag";
import {
  getDeviceContext,
  getSessionContext,
  trackBugReportDismissed,
  trackBugReportFailed,
  trackBugReportOpened,
  trackBugReportSubmitted,
} from "@/lib/analytics";
import { collectBugReportContext } from "@/lib/bug-report/client-context";
import {
  canSubmitBugReport,
  MAX_DESCRIPTION_LEN,
  MAX_STEPS_LEN,
  SCREENSHOT_ACCEPT,
  validateScreenshot,
} from "@/lib/bug-report/report";

/**
 * Routes where a persistent floating control is not welcome. Deliberately tiny:
 * `/checkout` hosts a third-party payment form where an overlay near the edge
 * is a real hazard, and the two `/auth` routes below are transient redirect
 * screens nobody is looking at. Sign-in and sign-up are NOT here — a bug that
 * stops someone getting into their account is exactly the report we most need.
 */
const HIDDEN_PREFIXES = ["/checkout", "/auth/callback", "/auth/confirm"];

/** Objective route test — not an opinion about what the user wants. */
export function shouldRenderBugReportTab(
  pathname: string | null | undefined,
): boolean {
  if (!pathname) return true;
  return !HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

type Status = "idle" | "submitting" | "success" | "error";

/** Turn a failed response into something a person can act on. */
export function submissionErrorMessage(
  status: number | null,
  message?: string | null,
): string {
  if (message && message.trim()) return message.trim();
  if (status === 429) {
    return "That's a lot of reports at once. Give it a minute and try again.";
  }
  if (status === null) {
    return "We couldn't reach Panda. Check your connection and try again — your report is still here.";
  }
  return "Something went wrong sending that. Your report is still here — try again.";
}

/**
 * True only once the client has hydrated. `useSyncExternalStore` rather than a
 * `setState` in an effect: the server snapshot is `false` and the client
 * snapshot is `true`, so React renders nothing on the server and the tab on the
 * client without a hydration mismatch — and without a cascading extra render.
 */
function useHasHydrated(): boolean {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
}

/**
 * True while a modal dialog is open anywhere in the app.
 *
 * The tab used to claim it sat "below the app's dialog layer". It did not: at
 * `z-[90]` it painted over the mobile chat sheet (`z-60`), over the full-view
 * preview (`z-80`) and over Grow's own canvas modals (`z-20` in a container
 * that opens no stacking context) — a floating control on top of a sheet the
 * user had deliberately opened. Raising or lowering the number chases a layer
 * that keeps growing, so the tab yields instead: while something modal is up,
 * it is not on screen at all.
 *
 * This reads one objective DOM fact — an element is marked `aria-modal="true"`
 * or it is not — and never what any of it says.
 *
 * The bug panel is excluded by its own marker rather than by being tolerated:
 * were it counted, closing it would leave this `true` for a tick, the tab
 * would still be `display:none` when the panel handed focus back, and the
 * keyboard route out would end on nothing.
 */
const OTHER_MODAL = '[aria-modal="true"]:not([data-bug-report-panel])';

function useModalIsOpen(): boolean {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const read = () => setModalOpen(!!document.querySelector(OTHER_MODAL));
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-modal"],
    });
    return () => observer.disconnect();
  }, []);

  return modalOpen;
}

export function BugReportTab() {
  const pathname = usePathname();
  const mounted = useHasHydrated();
  const modalIsOpen = useModalIsOpen();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const tabRef = useRef<HTMLButtonElement | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /**
   * Stable for the life of ONE submission, retries included. The server keys
   * its duplicate check on this, so a retry after a lost response is recognised
   * as the same report rather than filed (and posted) twice. Cleared only after
   * a confirmed success, so the NEXT report is a new one.
   */
  const reportIdRef = useRef<string | null>(null);
  const attemptRef = useRef(0);

  const titleId = useId();
  const descriptionFieldId = useId();
  const stepsFieldId = useId();
  const errorId = useId();

  const projectId = projectIdFromPathname(pathname);
  const route = routeTagFor(pathname) ?? null;

  const close = useCallback(() => {
    setOpen(false);
    // A failed attempt keeps its text AND its report id: reopening and pressing
    // send again is a retry of the same report, not a second one. Only a
    // confirmed send resets the panel.
    if (status === "success") {
      setStatus("idle");
      return;
    }
    trackBugReportDismissed({
      project_id: projectId,
      had_text: description.trim().length > 0,
    });
  }, [status, projectId, description]);

  const openPanel = useCallback(() => {
    setOpen(true);
    if (status === "success") {
      // Coming back after a confirmed send starts a clean report.
      setStatus("idle");
      setErrorMessage(null);
    }
    trackBugReportOpened({ project_id: projectId, route });
  }, [status, projectId, route]);

  // Escape closes, Tab stays inside, focus starts in the description box and
  // returns to the tab on close — the panel is a dialog, so it behaves like one.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Captured now: reading tabRef.current in the cleanup would read whatever
    // the ref points at by then, not the control this panel was opened from.
    const tab = tabRef.current;
    // Let the panel paint before moving focus, so mobile keyboards open against
    // a laid-out sheet rather than one mid-animation.
    const focusTimer = window.setTimeout(
      () => descriptionRef.current?.focus(),
      0,
    );

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      (previouslyFocused ?? tab)?.focus?.();
    };
  }, [open]);

  const pickFile = useCallback((picked: File | null) => {
    if (!picked) {
      setFile(null);
      setFileError(null);
      return;
    }
    const check = validateScreenshot(picked);
    if (!check.ok) {
      setFile(null);
      setFileError(check.error);
      return;
    }
    setFile(picked);
    setFileError(null);
  }, []);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      // Duplicate-click protection, first line: a send already in flight owns
      // the form. The report id below is the second line, and the server's
      // unique index is the third.
      if (status === "submitting") return;
      if (!canSubmitBugReport(description)) {
        descriptionRef.current?.focus();
        return;
      }

      if (!reportIdRef.current) reportIdRef.current = uuidv4();
      attemptRef.current += 1;
      setStatus("submitting");
      setErrorMessage(null);

      const session = getSessionContext();
      const context = collectBugReportContext(getDeviceContext());

      const body = new FormData();
      body.append(
        "payload",
        JSON.stringify({
          reportId: reportIdRef.current,
          description,
          steps,
          projectId,
          pageUrl: context.pageUrl ?? null,
          pagePath: context.pagePath ?? null,
          sessionId: session.session_id ?? null,
          sessionReplayUrl: session.session_replay_url ?? null,
          context,
        }),
      );
      if (file) body.append("screenshot", file);

      try {
        const response = await fetch("/api/bug-reports", {
          method: "POST",
          body,
        });
        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as {
            message?: string;
            error?: string;
          } | null;
          setStatus("error");
          setErrorMessage(
            submissionErrorMessage(response.status, payload?.message),
          );
          trackBugReportFailed({
            project_id: projectId,
            status: response.status,
            reason: payload?.error ?? "http_error",
            attempt: attemptRef.current,
          });
          return;
        }
        const payload = (await response.json().catch(() => ({}))) as {
          duplicate?: boolean;
        };
        trackBugReportSubmitted({
          project_id: projectId,
          route,
          has_screenshot: !!file,
          has_steps: steps.trim().length > 0,
          attempt: attemptRef.current,
          duplicate: !!payload.duplicate,
        });
        // Only a confirmed send clears the draft — and the report id with it.
        setStatus("success");
        setDescription("");
        setSteps("");
        setFile(null);
        setFileError(null);
        reportIdRef.current = null;
        attemptRef.current = 0;
        if (fileInputRef.current) fileInputRef.current.value = "";
      } catch {
        setStatus("error");
        setErrorMessage(submissionErrorMessage(null));
        trackBugReportFailed({
          project_id: projectId,
          status: null,
          reason: "network_error",
          attempt: attemptRef.current,
        });
      }
    },
    [status, description, steps, file, projectId, route],
  );

  if (!mounted) return null;
  if (!shouldRenderBugReportTab(pathname)) return null;

  const submitting = status === "submitting";
  const canSend = canSubmitBugReport(description) && !submitting;

  return createPortal(
    <>
      {/* Not rendered at all while another modal owns the screen: not hidden
          with a class, which a `touch:`-gated display utility would win back,
          and not merely made transparent, which would leave it in the tab
          order behind a dialog. Our OWN panel is not "another modal" — the tab
          stays mounted under it so the panel can hand focus back to it.

          The tab. Edge-anchored and vertically centred so it sits in the
          gutter beside the page rather than over any control, and gone
          entirely while something modal is up.

          ── Why a phone gets a different shape ─────────────────────────────
          A desktop window has a gutter; a phone does not. Measured at 390px
          in Grow: the labelled tab is 32px wide, and the canvas's own right
          padding plus a card's is 30px — so the label landed ON the card's
          status column, took the card's right border with it, and made a card
          that had been read look clipped. That is the reported bug, and moving
          this one tab down the screen would only pick a different row for it
          to sit on.

          Under a finger it is the icon alone at 28px: narrower than the 30px
          of padding every Grow card already has to its right, which is what
          `playwright/tests/responsive/grow.spec.ts` holds it to on every
          screen in the flow. The label stays its accessible name, and comes
          back as text wherever there is room for it.

          Keyed on the pointer rather than on `sm`, because a phone in
          landscape is 844px wide: a width test would put the full label back
          on the screen with the LEAST room for it. */}
      {modalIsOpen && !open ? null : (
        <button
          ref={tabRef}
          type="button"
          onClick={() => (open ? close() : openPanel())}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Report a bug"
          className={cn(
            "fixed right-0 top-1/2 z-[90] -translate-y-1/2 border border-r-0",
            // The labelled tab, bounded like every other viewport-anchored
            // surface — on a short screen it is otherwise taller than the room
            // it has.
            "max-h-[80dvh] overflow-hidden rounded-l-xl py-3 pr-1.5 pl-2",
            // Touch: an icon-only puck that fits inside the gutter.
            "touch:grid touch:h-14 touch:max-h-none touch:w-7 touch:place-items-center",
            "touch:rounded-l-lg touch:p-0",
            "border-zinc-200 bg-white/95 shadow-lg backdrop-blur",
            "text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600",
            "dark:border-[#334155] dark:bg-[#18212f]/95 dark:text-[#94a3b8]",
            "dark:hover:bg-[#1f2b3d] dark:hover:text-[#f1f5f9]",
            open && "pointer-events-none opacity-0",
          )}
        >
          <span className="flex flex-col items-center gap-2">
            <Bug className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span
              className="text-[11px] font-semibold tracking-wide [writing-mode:vertical-rl] touch:hidden"
              style={{ transform: "rotate(180deg)" }}
            >
              Report a bug
            </span>
          </span>
        </button>
      )}

      {open && (
        <>
          {/* Dismissible scrim. The panel is a dialog, so it takes focus while
              it is open — one tap, Escape, or the close button gives the page
              straight back. */}
          <div
            className="fixed inset-0 z-[9998] bg-black/40 sm:bg-black/20"
            onClick={close}
            aria-hidden="true"
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            data-bug-report-panel=""
            aria-labelledby={titleId}
            className={cn(
              // The shared containment frame: bounded in dvh and never itself
              // the scroller, so the header and the send button stay pinned
              // while the fields scroll between them — including when a mobile
              // keyboard takes half the screen.
              MODAL_VIEWPORT_FRAME,
              // Mobile: a bottom sheet.
              "fixed inset-x-0 bottom-0 z-[9999]",
              "rounded-t-2xl border border-zinc-200 bg-white shadow-2xl",
              "dark:border-[#334155] dark:bg-[#18212f]",
              // Desktop: a compact panel beside the tab it came from.
              "sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-1/2 sm:w-[380px]",
              "sm:max-h-[min(calc(100dvh-2rem),42rem)] sm:-translate-y-1/2 sm:rounded-2xl",
            )}
          >
            <div className="flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4 dark:border-[#243044]">
              <div>
                <h2
                  id={titleId}
                  className="flex items-center gap-2 text-base font-bold text-zinc-900 dark:text-[#f1f5f9]"
                >
                  <Bug className="h-4 w-4 text-amber-500" aria-hidden="true" />
                  Report a bug
                </h2>
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-[#94a3b8]">
                  {status === "success"
                    ? "Thanks — the team has it."
                    : "Tell us what happened. We'll pick it up from here."}
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close bug report"
                className="-mr-1 rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:hover:bg-[#334155] dark:hover:text-zinc-300"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            {status === "success" ? (
              <div className="flex flex-col items-center gap-3 px-5 py-8 text-center">
                <CheckCircle2
                  className="h-9 w-9 text-emerald-500"
                  aria-hidden="true"
                />
                <p
                  role="status"
                  className="text-sm font-semibold text-zinc-900 dark:text-[#f1f5f9]"
                >
                  Report sent
                </p>
                <p className="text-xs text-zinc-500 dark:text-[#94a3b8]">
                  We&apos;ve logged what you told us, along with the page you
                  were on. Nothing in your project changed.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-2 rounded-xl bg-panda-gradient-success px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-900/20 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                >
                  Back to work
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className={cn(
                  MODAL_SCROLL_BODY,
                  "flex flex-col gap-4 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]",
                )}
              >
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor={descriptionFieldId}
                    className="text-xs font-semibold text-zinc-700 dark:text-[#cbd5e1]"
                  >
                    What went wrong?
                    <span className="ml-1 font-normal text-rose-500" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <textarea
                    ref={descriptionRef}
                    id={descriptionFieldId}
                    required
                    rows={4}
                    maxLength={MAX_DESCRIPTION_LEN}
                    value={description}
                    disabled={submitting}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="The preview went blank after I asked for a dark theme."
                    className="w-full resize-none rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 disabled:opacity-60 dark:border-[#334155] dark:bg-[#111a27] dark:text-[#f1f5f9] dark:placeholder:text-[#64748b]"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor={stepsFieldId}
                    className="text-xs font-semibold text-zinc-700 dark:text-[#cbd5e1]"
                  >
                    How can we reproduce it?{" "}
                    <span className="font-normal text-zinc-400 dark:text-[#64748b]">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    id={stepsFieldId}
                    rows={3}
                    maxLength={MAX_STEPS_LEN}
                    value={steps}
                    disabled={submitting}
                    onChange={(e) => setSteps(e.target.value)}
                    placeholder="What you did just before it happened."
                    className="w-full resize-none rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 disabled:opacity-60 dark:border-[#334155] dark:bg-[#111a27] dark:text-[#f1f5f9] dark:placeholder:text-[#64748b]"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-zinc-700 dark:text-[#cbd5e1]">
                    Screenshot{" "}
                    <span className="font-normal text-zinc-400 dark:text-[#64748b]">
                      (optional)
                    </span>
                  </span>
                  <input
                    ref={fileInputRef}
                    id="panda-bug-report-screenshot"
                    type="file"
                    accept={SCREENSHOT_ACCEPT}
                    disabled={submitting}
                    aria-label="Attach a screenshot"
                    onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="panda-bug-report-screenshot"
                    className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-zinc-300 px-3 py-2.5 text-xs font-medium text-zinc-600 transition-colors hover:border-zinc-400 hover:bg-zinc-50 focus-within:ring-2 focus-within:ring-emerald-600 dark:border-[#334155] dark:text-[#94a3b8] dark:hover:bg-white/[0.04]"
                  >
                    <ImagePlus className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="truncate">
                      {file ? file.name : "Add an image (PNG, JPEG, WebP, GIF)"}
                    </span>
                  </label>
                  {file && (
                    <button
                      type="button"
                      onClick={() => {
                        pickFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="self-start text-[11px] font-medium text-zinc-500 underline-offset-2 hover:underline dark:text-[#94a3b8]"
                    >
                      Remove screenshot
                    </button>
                  )}
                  {fileError && (
                    <p className="text-[11px] font-medium text-rose-600 dark:text-rose-400">
                      {fileError}
                    </p>
                  )}
                </div>

                <p className="text-[11px] leading-relaxed text-zinc-400 dark:text-[#64748b]">
                  {/* One JS string, not JSX text around an expression: JSX
                      collapses the whitespace at a line break, which silently
                      ran "on" into "and" when there was no project. */}
                  {`We'll include the page you're on${
                    projectId ? ", the project you're in," : ""
                  } and your browser details, so you don't have to. Sending a report doesn't change your app or use credits.`}
                </p>

                {status === "error" && errorMessage && (
                  <div
                    id={errorId}
                    role="alert"
                    className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300"
                  >
                    <TriangleAlert
                      className="mt-0.5 h-3.5 w-3.5 shrink-0"
                      aria-hidden="true"
                    />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSend}
                  aria-describedby={status === "error" ? errorId : undefined}
                  className="mt-auto flex items-center justify-center gap-2 rounded-xl bg-panda-gradient-success px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-900/20 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                >
                  {submitting && (
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {submitting
                    ? "Sending…"
                    : status === "error"
                      ? "Try again"
                      : "Send report"}
                </button>
              </form>
            )}
          </div>
        </>
      )}
    </>,
    document.body,
  );
}
